import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Paper,
  Chip,
  Grid,
  Alert,
  Skeleton,
  Stack,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import assetService from '../api/assetService';
import AssetThumbnail from '../components/common/AssetThumbnail';
import VideoThumbnail from '../components/common/VideoThumbnail';
import AssetCard from '../components/asset/AssetCard';
import { METADATA_LABEL_KEYS } from '../constants/assetDetailMetadata';
import ReadOnlyMetadataGrid from '../components/common/ReadOnlyMetadataGrid';
import {
  AssetDetailsResponse,
  LookMetadataResponse,
  MoveMetadataResponse,
  SongMetadataDetailsResponse,
  StarMetadataResponse,
  TagsResponse,
  WorldMetadataResponse,
} from '../contracts/openapi/assetContracts';
import type { Asset } from '../types/asset.types';

const VARIANT_LAYERS = ['G', 'S', 'L', 'M', 'W'];

const LAYER_LABELS: Record<string, string> = {
  G: 'Song',
  S: 'Star',
  L: 'Look',
  M: 'Move',
  W: 'World',
  C: 'Composite',
  B: 'Brand',
  P: 'Personalize',
  T: 'Training',
  R: 'Rights',
};

const VARIANTS_PAGE_SIZE = 12;

/** Map raw layer search result to Asset for display (same field mapping as AssetSearch). */
function mapVariantResultToAsset(layer: string, item: any): Asset {
  const base = {
    id: item.id || item._id || '',
    _id: item.id || item._id,
    machineFriendlyName: item.machineFriendlyName ?? item.nnaAddress ?? '',
    description: item.description || '',
    categoryCode: item.categoryCode || item.categoryId?.toString() || '',
    subcategoryCode: item.subcategoryCode || item.subcategoryId?.toString() || '',
    files: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: '',
    updatedBy: '',
    status: 'active' as const,
    baseAsset: item.baseAsset,
  };
  if (layer === 'G') {
    return {
      ...base,
      name: item.songName || item.humanFriendlyName || '',
      friendlyName: item.humanFriendlyName || item.songName || '',
      layer: 'G',
      type: 'song',
      imageUrl: item.imageUrl || item.thumbnailUrl || '',
      metadata: { songName: item.songName, thumbnailUrl: item.thumbnailUrl, fileUrl: item.fileUrl },
      songMetadata: { songName: item.songName, artistName: item.artistName },
      albumArt: item.thumbnailUrl,
    };
  }
  if (layer === 'S') {
    return {
      ...base,
      name: item.starName || item.humanFriendlyName || '',
      friendlyName: item.humanFriendlyName || item.starName || '',
      layer: 'S',
      type: 'star',
      imageUrl: item.imageUrl || '',
      metadata: { starName: item.starName, imageUrl: item.imageUrl, fileUrl: item.fileUrl },
      starMetadata: { starName: item.starName },
      albumArt: item.imageUrl,
    };
  }
  if (layer === 'L') {
    return {
      ...base,
      name: item.outfitName || item.humanFriendlyName || '',
      friendlyName: item.humanFriendlyName || item.outfitName || '',
      layer: 'L',
      type: 'look',
      imageUrl: item.imageUrl || '',
      metadata: { outfitName: item.outfitName, imageUrl: item.imageUrl, fileUrl: item.fileUrl },
      lookMetadata: { outfitName: item.outfitName },
      albumArt: item.imageUrl,
    };
  }
  if (layer === 'M') {
    return {
      ...base,
      name: item.moveName || item.humanFriendlyName || '',
      friendlyName: item.humanFriendlyName || item.moveName || '',
      layer: 'M',
      type: 'move',
      imageUrl: item.fileUrl || item.videoUrl || item.video_url || '',
      metadata: { moveName: item.moveName, fileUrl: item.fileUrl },
      moveMetadata: { moveName: item.moveName },
      albumArt: item.fileUrl || item.videoUrl || item.video_url,
    };
  }
  if (layer === 'W') {
    const worldFileUrl = item.fileUrl || '';
    return {
      ...base,
      name: item.worldName || item.humanFriendlyName || '',
      friendlyName: item.humanFriendlyName || item.worldName || '',
      layer: 'W',
      type: 'world',
      imageUrl: worldFileUrl,
      metadata: { worldName: item.worldName, fileUrl: worldFileUrl },
      worldMetadata: { worldName: item.worldName },
      albumArt: worldFileUrl,
    };
  }
  return {
    ...base,
    name: item.humanFriendlyName || '',
    friendlyName: item.humanFriendlyName || '',
    layer: layer as any,
    type: 'base',
    imageUrl: item.imageUrl || item.fileUrl || '',
    metadata: {},
    albumArt: item.imageUrl || item.fileUrl,
  };
}

function MetadataSection({
  title,
  data,
  allKeys,
  renderFields,
  disablePaper = false,
}: {
  title: string;
  data: Record<string, unknown> | null | undefined;
  allKeys?: string[];
  renderFields?: (data: Record<string, unknown>) => React.ReactNode;
  disablePaper?: boolean;
}) {
  if (!data && !allKeys?.length) return null;
  const keys = allKeys ?? (data ? Object.keys(data as object) : []);
  if (keys.length === 0 && !renderFields) return null;
  const wrapperSx = { p: 3, mb: 2, ...(disablePaper ? { border: '1px solid', borderColor: 'divider', borderRadius: 1 } : {}) };
  const content = (
    <>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
        {title}
      </Typography>
      {renderFields ? (
        renderFields(data as Record<string, unknown>)
      ) : (
        <ReadOnlyMetadataGrid data={data} allKeys={keys} />
      )}
    </>
  );
  return disablePaper ? <Box sx={wrapperSx}>{content}</Box> : <Paper variant="outlined" sx={wrapperSx}>{content}</Paper>;
}

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<AssetDetailsResponse | null>(null);
  const [tags, setTags] = useState<TagsResponse | null>(null);
  const [layerMetadata, setLayerMetadata] = useState<
    LookMetadataResponse | MoveMetadataResponse | SongMetadataDetailsResponse | StarMetadataResponse | WorldMetadataResponse | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [baseAssetDetails, setBaseAssetDetails] = useState<AssetDetailsResponse | null>(null);
  const [baseAssetLoading, setBaseAssetLoading] = useState(false);
  const [baseAssetError, setBaseAssetError] = useState<string | null>(null);
  const [variants, setVariants] = useState<Asset[]>([]);
  const [variantsTotalCount, setVariantsTotalCount] = useState(0);
  const [variantsPage, setVariantsPage] = useState(0);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsError, setVariantsError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadData = useCallback(async (assetId: string) => {
    setLoading(true);
    setDetailsError(null);
    setTagsError(null);
    setMetadataError(null);
    try {
      const [detailsRes, tagsRes] = await Promise.all([
        assetService.getAssetDetails(assetId).catch((e) => {
          setDetailsError(e?.message ?? 'Failed to load asset details');
          return null;
        }),
        assetService.getAssetTags(assetId).catch((e) => {
          setTagsError(e?.message ?? 'Failed to load tags');
          return { tags: [] };
        }),
      ]);
      if (!detailsRes) {
        setLoading(false);
        return;
      }
      setDetails(detailsRes);
      setTags(tagsRes ?? { tags: [] });

      const layer = detailsRes.layer;
      if (['L', 'M', 'S', 'G', 'W'].includes(layer)) {
        try {
          if (layer === 'L') {
            const m = await assetService.getLookMetadata(assetId);
            setLayerMetadata(m);
          } else if (layer === 'M') {
            const m = await assetService.getMoveMetadata(assetId);
            setLayerMetadata(m);
          } else if (layer === 'G') {
            const m = await assetService.getSongMetadata(assetId);
            setLayerMetadata(m);
          } else if (layer === 'S') {
            const m = await assetService.getStarMetadata(assetId);
            setLayerMetadata(m);
          } else if (layer === 'W') {
            const m = await assetService.getWorldMetadata(assetId);
            setLayerMetadata(m);
          }
        } catch (e) {
          setMetadataError(e instanceof Error ? e.message : 'Failed to load metadata');
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) loadData(id);
  }, [id, loadData]);

  useEffect(() => {
    const baseId = details?.baseAssetId;
    if (!baseId) {
      setBaseAssetDetails(null);
      setBaseAssetError(null);
      setBaseAssetLoading(false);
      return;
    }
    let cancelled = false;
    setBaseAssetLoading(true);
    setBaseAssetError(null);
    assetService
      .getAssetDetails(baseId)
      .then((res) => {
        if (!cancelled) {
          setBaseAssetDetails(res);
          setBaseAssetError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setBaseAssetError(e?.message ?? 'Failed to load base asset');
          setBaseAssetDetails(null);
        }
      })
      .finally(() => {
        if (!cancelled) setBaseAssetLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [details?.baseAssetId]);

  const showVariantsSection =
    details &&
    VARIANT_LAYERS.includes(details.layer) &&
    !details.baseAssetId;

  useEffect(() => {
    if (!showVariantsSection || !details) {
      setVariants([]);
      setVariantsTotalCount(0);
      setVariantsError(null);
      return;
    }
    let cancelled = false;
    setVariantsLoading(true);
    setVariantsError(null);
    assetService
      .getVariantsByBaseAssetId(details.layer, details.id, variantsPage, VARIANTS_PAGE_SIZE)
      .then(({ results, totalCount }) => {
        if (!cancelled) {
          setVariants(results.map((item) => mapVariantResultToAsset(details.layer, item)));
          setVariantsTotalCount(totalCount);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setVariantsError(e?.message ?? 'Failed to load variants');
          setVariants([]);
          setVariantsTotalCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setVariantsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showVariantsSection, details?.id, details?.layer, variantsPage]);

  useEffect(() => {
    setVariantsPage(0);
  }, [details?.id]);

  const handleVariantsPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setVariantsPage(page - 1);
  };

  const loadVariants = () => {
    if (!details || !showVariantsSection) return;
    setVariantsError(null);
    setVariantsLoading(true);
    assetService
      .getVariantsByBaseAssetId(details.layer, details.id, variantsPage, VARIANTS_PAGE_SIZE)
      .then(({ results, totalCount }) => {
        setVariants(results.map((item) => mapVariantResultToAsset(details.layer, item)));
        setVariantsTotalCount(totalCount);
      })
      .catch((e) => {
        setVariantsError(e?.message ?? 'Failed to load variants');
      })
      .finally(() => setVariantsLoading(false));
  };

  const handleBack = () => navigate('/search-assets');
  const handleCreateVariant = () => {
    if (!id || !details) return;
    navigate(`/assets/${details.id}/create-variant`);
  };

  const showCreateVariant =
    details &&
    VARIANT_LAYERS.includes(details.layer) &&
    !details.baseAssetId;

  const canDelete =
    details &&
    (!showVariantsSection || (!variantsLoading && variantsTotalCount === 0));

  const openDeleteDialog = () => {
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (!deleteInProgress) {
      setDeleteDialogOpen(false);
      setDeleteError(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!id) return;
    setDeleteError(null);
    setDeleteInProgress(true);
    try {
      await assetService.deleteAsset(id);
      setDeleteDialogOpen(false);
      navigate('/search-assets');
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete asset');
    } finally {
      setDeleteInProgress(false);
    }
  };

  if (!id) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton onClick={handleBack} aria-label="Back to Browse Assets" edge="start" size="small">
                <ArrowBackIcon />
              </IconButton>
            </Box>
            <Alert severity="warning">Missing asset ID.</Alert>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (loading && !details) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            <Skeleton variant="rectangular" height={48} width={200} sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid item xs={12} md={8}>
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="text" width="40%" height={32} />
                <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (detailsError && !details) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton onClick={handleBack} aria-label="Back to Browse Assets" edge="start" size="small">
                <ArrowBackIcon />
              </IconButton>
            </Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailsError}
            </Alert>
            <Button variant="contained" onClick={() => loadData(id)}>
              Retry
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (!details) {
    return null;
  }

  const assetForThumbnail: Asset = {
    id: details.id,
    _id: details.id,
    name: details.humanFriendlyName ?? '',
    friendlyName: details.humanFriendlyName ?? '',
    machineFriendlyName: details.machineFriendlyAddress ?? '',
    layer: details.layer,
    categoryCode: details.categoryCode ?? details.categoryName ?? '',
    subcategoryCode: details.subcategoryCode ?? details.subcategoryName ?? '',
    type: 'base',
    imageUrl: details.fileUrl ?? '',
    files: [],
    metadata: {
      fileUrl: details.fileUrl,
      thumbnailUrl: (layerMetadata as SongMetadataDetailsResponse)?.thumbnailUrl,
    },
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    status: 'active',
  };

  const taxonomyPath = [
    details.categoryName ?? details.categoryCode,
    details.subcategoryName ?? details.subcategoryCode,
  ]
    .filter(Boolean)
    .join(' / ');

  const assetNameFromMetadata =
    layerMetadata &&
    (details.layer === 'G' ? (layerMetadata as SongMetadataDetailsResponse).songName
      : details.layer === 'L' ? (layerMetadata as LookMetadataResponse).outfitName
      : details.layer === 'M' ? (layerMetadata as MoveMetadataResponse).moveName
      : details.layer === 'S' ? (layerMetadata as StarMetadataResponse).starName
      : details.layer === 'W' ? (layerMetadata as WorldMetadataResponse).worldName
      : undefined);
  const displayTitle = assetNameFromMetadata ?? details.humanFriendlyName ?? details.id;
  const pageTitle = displayTitle;
  const mfa = details.machineFriendlyAddress;

  const baseAssetForThumbnail: Asset | null =
    baseAssetDetails
      ? {
          id: baseAssetDetails.id,
          _id: baseAssetDetails.id,
          name: baseAssetDetails.humanFriendlyName ?? '',
          friendlyName: baseAssetDetails.humanFriendlyName ?? '',
          machineFriendlyName: baseAssetDetails.machineFriendlyAddress ?? '',
          layer: baseAssetDetails.layer,
          categoryCode:
            baseAssetDetails.categoryCode ?? baseAssetDetails.categoryName ?? '',
          subcategoryCode:
            baseAssetDetails.subcategoryCode ??
            baseAssetDetails.subcategoryName ??
            '',
          type: 'base',
          imageUrl: baseAssetDetails.fileUrl ?? '',
          files: [],
          metadata: {
            fileUrl: baseAssetDetails.fileUrl,
          },
          createdAt: '',
          updatedAt: '',
          createdBy: '',
          status: 'active',
        }
      : null;

  return (
    <>
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header row: back arrow, title, chips, spacer, Create Variant, Delete */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 3, pb: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={handleBack} aria-label="Back to Browse Assets" edge="start" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            {pageTitle}
          </Typography>
          <Chip
            label={LAYER_LABELS[details.layer] ?? details.layer}
            color="primary"
            variant="outlined"
            size="medium"
          />
          {taxonomyPath && (
            <Chip label={taxonomyPath} variant="outlined" size="small" />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {VARIANT_LAYERS.includes(details.layer) && (
            <>
              <Tooltip title="Edit metadata">
                <IconButton component={Link} to={`/assets/${id}/edit`} color="primary" size="small" aria-label="Edit metadata">
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Replace file">
                <IconButton component={Link} to={`/assets/${id}/edit-file`} color="primary" size="small" aria-label="Replace file">
                  <CloudUploadIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          {showCreateVariant && (
            <Tooltip title="Create variant">
              <IconButton onClick={handleCreateVariant} color="secondary" size="small" aria-label="Create variant">
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <IconButton onClick={openDeleteDialog} color="error" size="small" aria-label="Delete">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, pb: 4 }}>
          <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box
                sx={{
                  height: 280,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                }}
              >
                {details.layer === 'C' && details.fileUrl ? (
                  <VideoThumbnail asset={assetForThumbnail} width={280} height={280} />
                ) : (
                  <AssetThumbnail asset={assetForThumbnail} width={280} height={280} />
                )}
              </Box>
            </Box>
            {details.baseAssetId && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                  Base asset
                </Typography>
                <Stack direction="column" alignItems="flex-start" spacing={1}>
                  <Box sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box
                      sx={{
                        width: 140,
                        height: 140,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                      }}
                    >
                      {baseAssetLoading && (
                        <Skeleton variant="rectangular" width={140} height={140} sx={{ borderRadius: 1 }} />
                      )}
                      {!baseAssetLoading && baseAssetError && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1, textAlign: 'center', fontSize: '0.75rem' }}>
                          {baseAssetError}
                        </Typography>
                      )}
                      {!baseAssetLoading && !baseAssetError && baseAssetForThumbnail && (
                        <AssetThumbnail asset={baseAssetForThumbnail} width={140} height={140} />
                      )}
                    </Box>
                  </Box>
                  <Button
                    component={Link}
                    to={`/assets/${details.baseAssetId}`}
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNewIcon />}
                  >
                    View base asset details
                  </Button>
                </Stack>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={8}>
            {details.baseAssetId && layerMetadata && (
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Variant name
                  </Typography>
                  <Typography variant="body2">
                    {(layerMetadata as { variantName?: string }).variantName ?? '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Variant type
                  </Typography>
                  <Typography variant="body2">
                    {(layerMetadata as { variantType?: string }).variantType ?? '—'}
                  </Typography>
                </Box>
              </Box>
            )}
            {details.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1">{details.description}</Typography>
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Tags
              </Typography>
              {tagsError ? (
                <Alert severity="warning" sx={{ py: 0 }}>{tagsError}</Alert>
              ) : !tags?.tags?.length ? (
                <Typography variant="body2" color="text.secondary">
                  No tags
                </Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {tags.tags.map((tag, i) => (
                    <Chip key={i} label={tag} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                HFN
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {details.humanFriendlyName ?? '—'}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                MFA
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {mfa ?? '—'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {metadataError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {metadataError}
            <Button size="small" onClick={() => id && loadData(id)} sx={{ ml: 1 }}>
              Retry
            </Button>
          </Alert>
        )}

        {layerMetadata && (
          <Box sx={{ mt: 3 }}>
            <MetadataSection
              title={`${LAYER_LABELS[details.layer] ?? details.layer} metadata`}
              data={
                details.layer === 'G'
                  ? (() => {
                      const { thumbnailUrl: _, thumbnailPath: __, ...rest } = layerMetadata as Record<string, unknown>;
                      return rest;
                    })()
                  : (layerMetadata as Record<string, unknown>)
              }
              allKeys={METADATA_LABEL_KEYS[details.layer]}
              disablePaper
            />
            {details.layer === 'G' && (layerMetadata as SongMetadataDetailsResponse).algorhythmMetadata && (
              <Box sx={{ mt: 2 }}>
                <MetadataSection
                  title="Algorhythm metadata"
                  data={(layerMetadata as SongMetadataDetailsResponse).algorhythmMetadata as Record<string, unknown>}
                  allKeys={['targetAudience', 'ageAppropriateness', 'culturalContext', 'musicalStyle', 'performanceContext']}
                  disablePaper
                />
              </Box>
            )}
          </Box>
        )}

      {showVariantsSection && (
        <Box sx={{ p: 4, mt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
            Variants
          </Typography>
          {variantsLoading && (
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                </Grid>
              ))}
            </Grid>
          )}
          {!variantsLoading && variantsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {variantsError}
              <Button size="small" onClick={loadVariants} sx={{ ml: 1 }}>
                Retry
              </Button>
            </Alert>
          )}
          {!variantsLoading && !variantsError && variants.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No variants yet.
            </Typography>
          )}
          {!variantsLoading && !variantsError && variants.length > 0 && (
            <>
              <Grid container spacing={2}>
                {variants.map((asset) => (
                  <Grid item xs={12} sm={6} md={4} key={asset.id}>
                    <AssetCard asset={asset} showActions={false} />
                  </Grid>
                ))}
              </Grid>
              {variantsTotalCount > VARIANTS_PAGE_SIZE && (
                <Stack alignItems="center" sx={{ mt: 3 }}>
                  <Pagination
                    count={Math.ceil(variantsTotalCount / VARIANTS_PAGE_SIZE)}
                    page={variantsPage + 1}
                    onChange={handleVariantsPageChange}
                    color="primary"
                  />
                </Stack>
              )}
            </>
          )}
        </Box>
      )}
        </Box>
      </Paper>
    </Box>

    <Dialog
      open={deleteDialogOpen}
      onClose={closeDeleteDialog}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={deleteInProgress}
    >
      <DialogTitle>Delete asset</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          This action cannot be undone. The asset &quot;{details?.humanFriendlyName ?? details?.id ?? ''}&quot; will be permanently deleted.
        </Typography>
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDeleteDialog} disabled={deleteInProgress}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDelete}
          color="error"
          variant="contained"
          disabled={deleteInProgress}
          startIcon={deleteInProgress ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        >
          {deleteInProgress ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
